import type { PageSummary } from '@/api/client';
import { API } from '@/config';
import { AuthApiError } from '@/features/auth/api';
import type { ProposalKind, ProposalResponse } from '@/features/propose/api';

export type ProposalListResponse = {
  proposals: ProposalResponse[];
  next_cursor?: string;
};

export type DiffLineType = 'equal' | 'delete' | 'insert';

export type DiffLine = {
  type: DiffLineType;
  old_line?: number;
  new_line?: number;
  content: string;
};

export type DiffHunk = {
  old_start: number;
  old_lines: number;
  new_start: number;
  new_lines: number;
  lines: DiffLine[];
};

export type ProposalDiffResponse = {
  proposal_id: string;
  kind: ProposalKind;
  page_id?: string;
  base_revision?: string;
  hunks: DiffHunk[];
};

export type RevisionPublishResponse = {
  id: string;
  page_id: string;
  base_revision?: string;
  slug: string;
  title: string;
  markdown?: string;
  rendered_html?: string;
  media_urls?: string[];
  published_by: string;
  published_at: string;
};

export type ProposalApprovalResponse = {
  proposal: ProposalResponse;
  page: PageSummary;
  revision: RevisionPublishResponse;
};

export type AuditEntry = {
  id: string;
  at: string;
  actor_id: string;
  actor_name: string;
  action: string;
  target_type: string;
  target_id: string;
  summary?: string;
};

export type AuditLogResponse = {
  entries: AuditEntry[];
  next_before?: string;
};

export type AuditLogQuery = {
  limit?: number;
  before?: string;
};

export type ProposalListQuery = {
  limit?: number;
  cursor?: string;
  lang?: string;
};

type ApiEnvelope<T> = {
  ok?: boolean;
  data?: T;
  error?: unknown;
};

export type ModerationApiClient = {
  listPendingProposals(query?: ProposalListQuery): Promise<ProposalListResponse>;
  getProposalDiff(id: string): Promise<ProposalDiffResponse>;
  approveProposal(id: string): Promise<ProposalApprovalResponse>;
  rejectProposal(id: string): Promise<ProposalResponse>;
  listAuditLog(query?: AuditLogQuery): Promise<AuditLogResponse>;
};

export function createModerationApiClient(
  getToken: () => string | null,
  baseURL = API.wikiBase,
): ModerationApiClient {
  const normalizedBaseURL = baseURL.replace(/\/+$/, '');

  return {
    listPendingProposals(query) {
      const token = requireToken(getToken());

      return request<ProposalListResponse>(
        `${normalizedBaseURL}/moderation/proposals${queryString(query)}`,
        {
          headers: authorizedHeaders(token),
        },
      );
    },
    getProposalDiff(id) {
      const token = requireToken(getToken());

      return request<ProposalDiffResponse>(
        `${normalizedBaseURL}/moderation/proposals/${encodeURIComponent(id)}/diff`,
        {
          headers: authorizedHeaders(token),
        },
      );
    },
    approveProposal(id) {
      const token = requireToken(getToken());

      return request<ProposalApprovalResponse>(
        `${normalizedBaseURL}/moderation/proposals/${encodeURIComponent(id)}/approve`,
        {
          method: 'POST',
          headers: authorizedHeaders(token),
        },
      );
    },
    rejectProposal(id) {
      const token = requireToken(getToken());

      return request<ProposalResponse>(
        `${normalizedBaseURL}/moderation/proposals/${encodeURIComponent(id)}/reject`,
        {
          method: 'POST',
          headers: authorizedHeaders(token),
        },
      );
    },
    listAuditLog(query) {
      const token = requireToken(getToken());

      return request<AuditLogResponse>(
        `${normalizedBaseURL}/moderation/audit${auditQueryString(query)}`,
        {
          headers: authorizedHeaders(token),
        },
      );
    },
  };
}

function requireToken(token: string | null): string {
  if (!token) {
    throw new AuthApiError('error_moderator_required', 401);
  }

  return token;
}

function authorizedHeaders(token: string): Record<string, string> {
  return {
    Accept: 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

async function request<T>(input: string, init: RequestInit): Promise<T> {
  const response = await fetch(input, init);
  const body = await parseBody(response);

  if (!response.ok) {
    throw new AuthApiError(errorMessage(body, response.statusText), response.status);
  }

  if (isEnvelope<T>(body)) {
    if (body.ok === false) {
      throw new AuthApiError(errorMessage(body, response.statusText), response.status);
    }

    if ('data' in body) {
      return body.data as T;
    }
  }

  return body as T;
}

async function parseBody(response: Response): Promise<unknown> {
  const text = await response.text();

  if (!text) {
    return {};
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    return text;
  }
}

function isEnvelope<T>(body: unknown): body is ApiEnvelope<T> {
  return Boolean(body && typeof body === 'object' && 'ok' in body);
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const error = (body as { error?: unknown }).error;

    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    if (error && typeof error === 'object' && 'message' in error) {
      const message = (error as { message?: unknown }).message;

      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
  }

  return fallback || 'request failed';
}

function auditQueryString(query?: AuditLogQuery): string {
  if (!query) {
    return '';
  }

  const params = new URLSearchParams();

  if (query.limit != null) {
    params.set('limit', String(query.limit));
  }

  if (query.before) {
    params.set('before', query.before);
  }

  const serialized = params.toString();

  return serialized ? `?${serialized}` : '';
}

function queryString(query?: ProposalListQuery): string {
  if (!query) {
    return '';
  }

  const params = new URLSearchParams();

  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === '') {
      continue;
    }

    params.set(key, String(value));
  }

  const serialized = params.toString();

  return serialized ? `?${serialized}` : '';
}
